/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualCategoriesState {
    version: Dtos.Version | null;
    categories: Record<number, Dtos.Category>;
    view: number[];
    loadState: IRequestState;
}

interface IInitialCategoriesState extends Record<number, IIndividualCategoriesState> {
}

export interface ICategoriesState {
   categories: IInitialCategoriesState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const CategoriesInitialIndividualState: IIndividualCategoriesState = {
    categories: {},
    version: null,
    view: [],
    loadState: {
        state: RequestState.None
    }
};

const CategoriesInitialState: IInitialCategoriesState = {
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useCategoriesSelector: TypedUseSelectorHook<ICategoriesState> = useSelector;

export const CategoriesSelectors = {
    selectCategoriesVersion: (state: ICategoriesState, versionId: number) => state.categories[versionId] ? state.categories[versionId].version : CategoriesInitialIndividualState.version,
    selectCategoriesView: (state: ICategoriesState, versionId: number) => state.categories[versionId] ? state.categories[versionId].view : CategoriesInitialIndividualState.view,
    selectCategory: (state: ICategoriesState, versionId: number, categoryId: number) => state.categories[versionId]  && state.categories[versionId].categories[categoryId] ? state.categories[versionId].categories[categoryId] : null,
    selectCategoriesLoadState: (state: ICategoriesState, versionId: number) => state.categories[versionId] ? state.categories[versionId].loadState : CategoriesInitialIndividualState.loadState,
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class CategoryReducer extends ImmerReducer<IInitialCategoriesState> {
    public loadByVersionId(versionId: number) {
        if (!this.draftState[versionId]) {
            this.draftState[versionId] = {...CategoriesInitialIndividualState};
        }

        this.draftState[versionId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, categories: Dtos.Category[]) {
        if (!this.draftState[version.id]) {
            this.draftState[version.id] = {...CategoriesInitialIndividualState};
        }

        this.draftState[version.id].version = version;

        this.draftState[version.id].categories = {};

        this.draftState[version.id].view = [];

        for (const category of categories) {
            this.draftState[version.id].categories[category.id] = category;
            this.draftState[version.id].view.push(category.id);
        }

        this.draftState[version.id].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(versionId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[versionId]) {
            this.draftState[versionId] = {...CategoriesInitialIndividualState};
        }

        this.draftState[versionId].version = CategoriesInitialIndividualState.version;
        this.draftState[versionId].categories = CategoriesInitialIndividualState.categories;
        this.draftState[versionId].view = CategoriesInitialIndividualState.view;
        this.draftState[versionId].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(versionId: number) {
        delete this.draftState[versionId];
    }

    public clearAll() {
        return {...CategoriesInitialState};
    }
}

export const CategoriesActions = createActionCreators(CategoryReducer);
const reducer = createReducerFunction(CategoryReducer, CategoriesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const CategoriesApi = {
    loadByVersionId: createGetRequest(
        Dtos.GetCategoriesByVersionId,
        (versionId: number) => ({
            versionId
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const CategoriesLogic = {
    loadByVersionId: createLogic({
        type: CategoriesActions.loadByVersionId.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<ICategoriesState, typeof CategoriesActions.loadByVersionId>, dispatch, done) => {
            try {
                const response = await CategoriesApi.loadByVersionId(action.payload);

                dispatch(CategoriesActions.loadSuccess(response.version, response.categories));
            }
            catch (error) {
                dispatch(CategoriesActions.loadFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    CategoriesLogic.loadByVersionId
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('categories', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/categories.ts